import React, { useState } from 'react';
import { Goal } from '../../services/goalService';
import { useTranslation } from '../../i18n';
import Icon from '../Icon';
import './CompletedGoalsGroup.css';

/** Where the group sits; each place remembers its own fold. */
export type CompletedGoalsPlace = 'home' | 'goals';

const storageKey = (place: CompletedGoalsPlace) => `riselio.completedGoalsOpen.${place}`;

const readOpen = (place: CompletedGoalsPlace): boolean => {
  try {
    return sessionStorage.getItem(storageKey(place)) === '1';
  } catch {
    return false;
  }
};

const writeOpen = (place: CompletedGoalsPlace, open: boolean) => {
  try {
    sessionStorage.setItem(storageKey(place), open ? '1' : '0');
  } catch {
    // Blocked storage: the fold just is not remembered
  }
};

/** [goals] split into the open ones and the completed ones, each keeping its order. */
export const splitCompleted = (goals: Goal[]) => ({
  active: goals.filter(g => !g.completed),
  completed: goals.filter(g => g.completed),
});

interface CompletedGoalsGroupProps {
  place: CompletedGoalsPlace;
  goals: Goal[];
  renderCard: (goal: Goal) => React.ReactNode;
  /** The layout class of the list above, so the cards line up with it. */
  listClassName: string;
  /** Nothing open above: show the cards, no fold. */
  alwaysOpen?: boolean;
}

/**
 * The completed goals under the open ones (T-127, as the app's T-123): a fold header
 * "Completed Goals (n)", closed by default and remembered for the session per page, the cards
 * when open. With nothing open above it the cards show without a fold.
 */
const CompletedGoalsGroup: React.FC<CompletedGoalsGroupProps> = ({
  place, goals, renderCard, listClassName, alwaysOpen = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(() => readOpen(place));
  if (goals.length === 0) return null;
  const shown = alwaysOpen || open;
  const title = `${t('achievements.completedGoals.title')} (${goals.length})`;
  const listId = `completed-goals-${place}`;

  const toggle = () => {
    setOpen(o => {
      writeOpen(place, !o);
      return !o;
    });
  };

  return (
    <section className="completed-goals-group">
      {alwaysOpen ? (
        <h3 className="completed-goals-group__header completed-goals-group__header--static">
          <Icon name="circleCheck" className="completed-goals-group__icon" />
          <span>{title}</span>
        </h3>
      ) : (
        <button
          type="button"
          className="completed-goals-group__header"
          aria-expanded={shown}
          aria-controls={listId}
          onClick={toggle}
        >
          <Icon name="circleCheck" className="completed-goals-group__icon" />
          <span className="completed-goals-group__title">{title}</span>
          <Icon name={shown ? 'chevronUp' : 'chevronDown'} className="completed-goals-group__chevron" />
        </button>
      )}
      {shown && (
        <div id={listId} className={listClassName}>
          {goals.map(goal => renderCard(goal))}
        </div>
      )}
    </section>
  );
};

export default CompletedGoalsGroup;
